// ── DELETE / VOID INTEGRITY (H3) ──────────────────────────────────────────
// A sale or purchase can have dependent records — payments, returns, an issued
// invoice, receivable/payable effects, stock movements, cash-ledger rows.
// Deleting the parent without handling those children leaves orphans that keep
// affecting stock, cash, COGS, receivables/payables and document history.
//
// This module derives, PURELY, what depends on a parent so the UI can show an
// exact preview and cascade correctly. It decides nothing about React/storage.
//
// Policy (enforced by the handlers that call this):
//   • No children  → delete allowed after a plain confirm.
//   • Has children → show a CASCADE PREVIEW; on confirm, soft-delete the parent
//     AND its child returns, and VOID (never erase) an issued invoice so its
//     number stays reserved. Cash needs no compensating entry — the ledger
//     derives from active records only.
//
// Locked down by deleteIntegrity.test.js.

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const nonCreditPayments = (payments) =>
  (payments || []).filter((p) => p && p.type !== 'credit' && num(p.amount) > 0);

const unitsOf = (rec) => {
  const items = Array.isArray(rec && rec.items) && rec.items.length > 0
    ? rec.items
    : [{ type: rec && rec.isService ? 'service' : 'product', quantity: rec && rec.quantity }];
  return items.reduce((s, it) => (it && it.type === 'service' ? s : s + num(it.quantity)), 0);
};

// Cash actually received on a record (non-credit payment lines).
const cashOf = (rec) => nonCreditPayments(rec && rec.payments).reduce((s, p) => s + num(p.amount), 0);

// Dependencies of a SALE (or service transaction).
export const getSaleDependencies = (sale, {
  salesReturns = [], issuedInvoices = [], manualInvoices = [],
} = {}) => {
  if (!sale) return { hasDependencies: false, paymentsCount: 0, returnsCount: 0, issuedDocumentCount: 0, affectedStockQty: 0, affectedCash: 0, returnsRefund: 0, returnIds: [], issuedInvoiceIds: [], isPaid: false, warnings: [] };
  const id = sale.id;
  const pays = nonCreditPayments(sale.payments);
  const returns = (salesReturns || []).filter((r) => sameId(r.originalSaleId, id));
  const issued = (issuedInvoices || []).filter((i) => sameId(i.id, id) || (sale.invoiceNumber && i.invoiceNumber === sale.invoiceNumber));
  const fromManual = (manualInvoices || []).filter((mi) => sameId(mi.convertedToSaleId, id));
  const affectedCash = cashOf(sale);
  const returnsRefund = returns.reduce((s, r) => s + num(r.cashRefundAmt != null ? r.cashRefundAmt : (r.refundAmt || r.totalRefund)), 0);
  const warnings = [];
  if (pays.length) warnings.push(`${pays.length} payment(s) totalling ${affectedCash}`);
  if (returns.length) warnings.push(`${returns.length} customer return(s)`);
  if (issued.length || sale.invoiceNumber) warnings.push(`issued invoice ${sale.invoiceNumber || ''}`.trim());
  if (fromManual.length) warnings.push(`converted from a manual invoice`);
  return {
    hasDependencies: pays.length > 0 || returns.length > 0 || issued.length > 0 || fromManual.length > 0,
    paymentsCount: pays.length,
    returnsCount: returns.length,
    issuedDocumentCount: issued.length,
    affectedStockQty: unitsOf(sale),
    affectedCash,
    returnsRefund,
    returnIds: returns.map((r) => r.id),
    issuedInvoiceIds: issued.map((i) => i.id),
    invoiceNumber: sale.invoiceNumber || null,
    isPaid: sale.isPaid !== false,
    warnings,
  };
};

// Dependencies of a PURCHASE.
export const getPurchaseDependencies = (purchase, { purchaseReturns = [] } = {}) => {
  if (!purchase) return { hasDependencies: false, paymentsCount: 0, returnsCount: 0, issuedDocumentCount: 0, affectedStockQty: 0, affectedCash: 0, returnsRefund: 0, returnIds: [], isPaid: false, warnings: [] };
  const id = purchase.id;
  const pays = nonCreditPayments(purchase.payments);
  const returns = (purchaseReturns || []).filter((r) => sameId(r.originalPurchaseId, id) || sameId(r.purchaseId, id));
  const affectedCash = cashOf(purchase);
  const returnsRefund = returns.reduce((s, r) => s + num(r.cashRefundAmt), 0);
  const warnings = [];
  if (pays.length) warnings.push(`${pays.length} payment(s) totalling ${affectedCash}`);
  if (returns.length) warnings.push(`${returns.length} supplier return(s)`);
  return {
    hasDependencies: pays.length > 0 || returns.length > 0,
    paymentsCount: pays.length,
    returnsCount: returns.length,
    issuedDocumentCount: 0,
    affectedStockQty: unitsOf(purchase),
    affectedCash,
    returnsRefund,
    returnIds: returns.map((r) => r.id),
    payableReduction: num(purchase.payableReducedByReturns),
    isPaid: purchase.isPaid !== false,
    warnings,
  };
};

// ── Return / parent consistency (returns durability) ──────────────────────
// A return must never be financially active when its parent is missing, deleted
// or mismatched. This pure validator classifies a return against the CURRENT
// parent records so calculations can quarantine invalid ones.
//   'valid'            — parent exists and is active.
//   'parent_deleted'   — parent is tombstoned (return should be too; quarantine).
//   'parent_missing'   — no parent id resolves (stale/orphan; quarantine).
//   'parent_id_mismatch' — return points at an id of the wrong kind.
export const validateReturnParent = (ret, { sales = [], purchases = [] } = {}) => {
  if (!ret) return 'parent_missing';
  const isSale = ret.originalSaleId != null && ret.originalSaleId !== '';
  const isPurchase = (ret.originalPurchaseId != null && ret.originalPurchaseId !== '') || (ret.purchaseId != null && ret.purchaseId !== '');
  const pid = isSale ? ret.originalSaleId : (ret.originalPurchaseId != null ? ret.originalPurchaseId : ret.purchaseId);
  if (pid == null || pid === '') return 'parent_missing';
  const parents = isSale ? sales : (isPurchase ? purchases : []);
  const parent = (parents || []).find((p) => sameId(p.id, pid));
  if (!parent) {
    // Look in the OTHER set — an id that resolves to the wrong kind is a mismatch.
    const other = (isSale ? purchases : sales) || [];
    if (other.find((p) => sameId(p.id, pid))) return 'parent_id_mismatch';
    return 'parent_missing';
  }
  if (parent.deleted) return 'parent_deleted';
  return 'valid';
};

// Active returns whose parent is currently valid — the ONLY returns that should
// feed cash/COGS/stock/AR/AP. `activeReturns` should already exclude tombstones.
export const validReturns = (activeReturns = [], { sales = [], purchases = [] } = {}) =>
  (activeReturns || []).filter((r) => validateReturnParent(r, { sales, purchases }) === 'valid');

// Returns that are active but NOT valid (orphaned / parent-deleted) — surfaced
// to a dev warning; never silently discarded.
export const quarantinedReturns = (activeReturns = [], { sales = [], purchases = [] } = {}) =>
  (activeReturns || [])
    .map((r) => ({ ret: r, status: validateReturnParent(r, { sales, purchases }) }))
    .filter((x) => x.status !== 'valid');

// Over-return guard: the returned quantity per original line must not exceed the
// quantity sold/purchased, across all ALREADY-active returns of the same parent.
// Returns { ok, reason } — call at creation time (and it is idempotent under
// merge because it counts active returns by id).
export const overReturnCheck = (candidate, parent, existingActiveReturns = []) => {
  if (!parent) return { ok: false, reason: 'parent_missing' };
  const parentUnits = (Array.isArray(parent.items) && parent.items.length > 0 ? parent.items : [{ productId: parent.productId, productName: parent.productName, quantity: parent.quantity }]);
  const soldByKey = {};
  parentUnits.forEach((it) => { soldByKey[String(it.productId != null ? it.productId : (it.productName || '').trim().toLowerCase())] = num(it.quantity); });
  const alreadyByKey = {};
  const otherReturns = (existingActiveReturns || []).filter((r) => r.id !== candidate.id);
  otherReturns.forEach((r) => {
    (Array.isArray(r.items) && r.items.length > 0 ? r.items : [{ productId: r.productId, productName: r.productName, qty: r.qty }]).forEach((it) => {
      const k = String(it.productId != null ? it.productId : (it.productName || '').trim().toLowerCase());
      alreadyByKey[k] = (alreadyByKey[k] || 0) + num(it.qty != null ? it.qty : it.quantity);
    });
  });
  const candItems = Array.isArray(candidate.items) && candidate.items.length > 0 ? candidate.items : [{ productId: candidate.productId, productName: candidate.productName, qty: candidate.qty }];
  for (const it of candItems) {
    const k = String(it.productId != null ? it.productId : (it.productName || '').trim().toLowerCase());
    const want = num(it.qty != null ? it.qty : it.quantity);
    const sold = soldByKey[k] || 0;
    const already = alreadyByKey[k] || 0;
    if (already + want > sold + 1e-6) return { ok: false, reason: 'over_returned', line: k, sold, alreadyReturned: already, requested: want };
  }
  return { ok: true };
};

// A human, itemized preview of what a delete/void will change (no generic
// "are you sure"). Returns an array of lines.
export const buildDeletePreview = (deps, { kind = 'sale', currencyFmt = (n) => String(n) } = {}) => {
  const lines = [];
  if (kind === 'sale') {
    lines.push(`• remove ${currencyFmt(deps.affectedCash)} received from Cash/Bank/Mobile`);
    if (deps.affectedStockQty) lines.push(`• restore ${deps.affectedStockQty} unit(s) to stock`);
    if (deps.returnsCount) lines.push(`• cancel ${deps.returnsCount} customer return(s) (refund ${currencyFmt(deps.returnsRefund)}, stock + COGS effects reversed)`);
    if (deps.invoiceNumber) lines.push(`• mark invoice ${deps.invoiceNumber} VOID (number stays reserved)`);
    lines.push(`• reduce/remove the customer receivable for any unpaid balance`);
  } else {
    lines.push(`• remove ${currencyFmt(deps.affectedCash)} paid from Cash/Bank/Mobile`);
    if (deps.affectedStockQty) lines.push(`• remove ${deps.affectedStockQty} unit(s) from stock`);
    if (deps.returnsCount) lines.push(`• cancel ${deps.returnsCount} supplier return(s)`);
    lines.push(`• reduce/remove the supplier payable for any unpaid balance`);
  }
  return lines;
};
